import { ModalBareboneProps, Text } from '@zeiss/beyond-online-react';
import { GeneralModal } from '@zeiss/lib-components';
import { Trans, useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { showToast } from 'src/helpers/showToast';
import { useUpdateTenantUsers } from 'src/hooks/api';
import { getUserInformation } from 'src/modules/TenantManagement/components/UserSearch';
import { StandardRouteParams } from 'src/types';
import { TenantUserResponse } from 'src/types/rest/tenant';

interface RemoveMemberModalProps extends Pick<
  ModalBareboneProps,
  'isOpen' | 'onClose'
> {
  data: TenantUserResponse & {
    tenantName: string;
  };
}

export const RemoveMemberModal = ({
  data: { memberSince, tenantName, ...user },
  isOpen,
  onClose,
}: RemoveMemberModalProps) => {
  const { t } = useTranslation(['common']);
  const { tenantId } = useParams<StandardRouteParams>();
  const { isPending, mutate: updateTenantUsers } = useUpdateTenantUsers(
    tenantId!,
  );

  const removeMember = () => {
    updateTenantUsers(
      {
        idsToRemove: [user.id],
      },
      {
        onError: () => {
          showToast({
            props: {
              title: t('common:notification.errorX', {
                action: t('common:action.removed'),
                x: t('common:glossary.member'),
              }),
              type: 'error',
            },
          });
        },
        onSuccess: () => {
          showToast({
            props: {
              title: t('common:notification.successX', {
                action: t('common:action.removed'),
                x: t('common:glossary.member'),
              }),
              type: 'success',
            },
          });

          onClose?.();
        },
      },
    );
  };

  const memberIdentifier = getUserInformation({ t, user });

  return (
    <GeneralModal
      acceptButtonText={t('common:action.remove')}
      declineButtonText={t('common:action.cancel')}
      headline={t('common:action.removeX', { x: t('common:glossary.member') })}
      isOpen={isOpen}
      loading={isPending}
      onAccept={removeMember}
      onClose={onClose}
      onDecline={onClose}
      size="m"
    >
      <Text>
        <Trans
          i18nKey="common:text.removeMemberX"
          t={t}
          values={{ memberName: memberIdentifier, tenantName }}
        >
          <b />
        </Trans>
      </Text>
    </GeneralModal>
  );
};
